import firebase from 'firebase';
import { convertHeicToJpeg, generateUniqueFilename, getFilenameAndExtension } from '@/utils/imageUtils';

const deleteImages = (images = []) => {
  const storage = firebase.storage();
  const refs = images.map(({ url }) => storage.refFromURL(url));
  return Promise.all(refs.map((ref) => ref.delete()));
};

const getBlobFromURL = (url) => {
  return new Promise((resolve, reject) => {
    try {
      var xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = function () {
        var blob = xhr.response;
        resolve(blob);
      };
      xhr.open('GET', url);
      xhr.send();
    } catch (error) {
      reject(error);
    }
  });
};

async function pictureDownload(downloadURLs) {
  const pictures = [];
  try {
    for (const downloadURL of downloadURLs) {
      let blob = await getBlobFromURL(downloadURL);

      if (blob.type === 'application/octet-stream') {
        blob = blob.slice(0, blob.size, 'image/heic');
      }

      const extension = `.${blob.type.split('/')[1]}`;

      const uniqueName = generateUniqueFilename(`downloaded-file${extension}`);

      pictures.push({
        fileName: uniqueName,
        file: blob,
      });
    }
    return pictures;
  } catch (error) {
    if (error?.error?.code === 404) {
      throw { code: error.error.code, message: 'File not found.' };
    }
    throw new Error(`Something went wrong downloading the ${downloadURLs.length > 1 ? 'pictures' : 'picture'}.`);
  }
}

async function pictureUpload(files, parents, errorHandler = undefined, fileRelationsMap = false) {
  const storage = firebase.storage();
  const uploadedPictures = [];

  try {
    await Promise.all(
      files.map(async (img) => {
        let file = img.file || img;
        if (!(file instanceof File)) {
          throw new Error(`Invalid file type: ${file}`);
        }
        const mimeType = file.type;
        let { fileName, extension } = getFilenameAndExtension(file.name);
        const isHeicExtension = extension.toLowerCase() === '.heic';
        const isHeicMime = mimeType && mimeType.toLowerCase() === 'image/heic';

        if (isHeicExtension || isHeicMime) {
          const convertedBlob = await convertHeicToJpeg(file);
          file = new File([convertedBlob], `${fileName}.jpg`, { type: 'image/jpeg' });
          extension = '.jpg';
        }

        const returnFileName = generateUniqueFilename(`${fileName}${extension}`);

        const uploadPath = `${parents}/${returnFileName}`;
        const uploadPointer = storage.ref().child(uploadPath);

        const snapshot = await uploadPointer.put(file);
        const downloadUrl = await snapshot.ref.getDownloadURL();

        if (fileRelationsMap) {
          uploadedPictures.push({
            fileName: returnFileName,
            url: downloadUrl,
            mimetype: file.type || 'unknown',
          });
        } else {
          uploadedPictures.push({
            name: returnFileName,
            url: downloadUrl,
            type: file.type || 'unknown',
          });
        }
      }),
    );
    return uploadedPictures;
  } catch (error) {
    console.error(error);
    if (typeof errorHandler === 'function') {
      await errorHandler(uploadedPictures);
    }
    throw error;
  }
}

/**
 * Uploads new pictures to firebase storage.
 * @param files
 * @param folder
 * @returns {Promise<*[]>}
 */
const picturesUploadNew = (files, folder) => {
  const pendingFiles = files.filter((f) => !f.url);
  const existingFiles = files.filter((f) => f.url);
  if (pendingFiles.length !== 0) return pictureUpload(pendingFiles, folder).then((uploadedFiles) => [...existingFiles, ...uploadedFiles]);
  else return Promise.resolve(existingFiles);
};

export { deleteImages, pictureUpload, picturesUploadNew, pictureDownload, getBlobFromURL };
